'use client'

import { useState } from 'react'
import Link from 'next/link'
import { CheckCircle } from 'lucide-react'

// TODO: Wire up the feedback form to a backend endpoint.
//   Suggested endpoint: POST /api/feedback
//   Body: { name, email, category, message }
//   Until then, the form submission is handled client-side only (logged to console).
//   See design-doc.md §15 for integration notes.

type FeedbackCategory = 'bug' | 'feature' | 'catalog' | 'general'

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'catalog', label: 'Catalog / data issue' },
  { value: 'general', label: 'General feedback' },
]

export default function FeedbackPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [category, setCategory] = useState<FeedbackCategory>('general')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    // TODO: replace with real API call when endpoint is ready
    // await api.post('/api/feedback', { name, email, category, message })
    await new Promise((r) => setTimeout(r, 600)) // simulate network
    console.log('Feedback submitted:', { name, email, category, message })
    setSubmitted(true)
    setLoading(false)
  }

  if (submitted) {
    return (
      <div className="bg-background">
        <section className="py-24">
          <div className="mx-auto max-w-md px-4 text-center sm:px-6">
            <div className="flex justify-center">
              <CheckCircle size={48} className="text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="mt-4 text-2xl font-bold text-foreground">Feedback received</h1>
            <p className="mt-3 text-secondary-foreground">
              Thanks for taking the time — it genuinely helps. We read every message.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link href="/" className="btn-primary justify-center">Back to home</Link>
              <button
                type="button"
                onClick={() => { setSubmitted(false); setMessage(''); setName(''); setEmail('') }}
                className="btn-ghost justify-center"
              >
                Send another
              </button>
            </div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="bg-background">

      {/* Hero */}
      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Feedback</p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
            Help us build a <em className="not-italic text-primary">better platform</em> for collectors.
          </h1>
          <p className="mt-4 text-base leading-7 text-secondary-foreground">
            Spotted a bug? Have a feature idea? Found a missing brand or variation? We want to hear it.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="card-shell space-y-5 p-6">

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="feedback-name" className="block text-sm font-medium text-foreground mb-1.5">
                  Name
                </label>
                <input
                  id="feedback-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="form-input"
                  autoComplete="name"
                />
              </div>
              <div>
                <label htmlFor="feedback-email" className="block text-sm font-medium text-foreground mb-1.5">
                  Email
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="form-input"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <p className="block text-sm font-medium text-foreground mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={`pill cursor-pointer transition-all duration-150 ${
                      category === cat.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'hover:border-primary/40'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="feedback-message" className="block text-sm font-medium text-foreground mb-1.5">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                placeholder="Tell us what's on your mind..."
                className="form-input resize-none h-auto py-3"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !message.trim()}
              className="btn-primary w-full justify-center"
            >
              {loading ? 'Sending…' : 'Send feedback'}
            </button>

            <p className="text-center text-xs text-muted-foreground">
              Your name and email are optional — we use them only to follow up if needed.
            </p>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Need help with your account?{' '}
            <Link href="/contact" className="text-primary font-medium hover:opacity-80 transition-opacity">
              Contact us instead →
            </Link>
          </p>
        </div>
      </section>
    </div>
  )
}
